<template>
  <Card>
    <CardHeader class="flex flex-row items-center justify-between gap-4">
      <CardTitle>History</CardTitle>
      <Button size="sm" variant="outline" :disabled="loadingHistory" @click="toggleHistory">
        {{ showHistory ? 'Hide History' : 'Show History' }}
      </Button>
    </CardHeader>
    <CardContent v-if="showHistory">
      <p v-if="loadingHistory" class="text-sm text-muted-foreground">
        Loading history...
      </p>
      <p v-else-if="historyEntries.length === 0" class="text-sm text-muted-foreground">
        No archived versions found.
      </p>
      <div v-else class="flex flex-col divide-y">
        <div
          v-for="entry in historyEntries"
          :key="entry.id"
          class="flex items-center justify-between gap-4 py-3"
        >
          <div class="min-w-0">
            <p class="truncate text-sm font-medium">
              v{{ formatVersion(entry.version) }} - {{ entry.title || 'Untitled Procedure' }}
            </p>
            <p class="truncate text-xs text-muted-foreground">
              {{ formatStatus(entry.status) }}
            </p>
          </div>

          <Button size="sm" variant="outline" as-child>
            <router-link
              :to="{
                name: 'ProcedureShowPage',
                params: {
                  businessSystem: normalizedBusinessSystemId,
                  procedure: entry.id,
                },
              }"
            >
              Open
            </router-link>
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../resources/js/components/ui/card/index.js'
import { useProcedureStore } from '../../stores/procedures.js'

const props = defineProps({
  businessSystemId: {
    type: [Number, String],
    required: true,
  },
  procedureId: {
    type: [Number, String],
    required: true,
  },
})

const procedureStore = useProcedureStore()
const showHistory = ref(false)
const loadingHistory = ref(false)

const normalizedBusinessSystemId = computed(() => Number(props.businessSystemId))
const normalizedProcedureId = computed(() => Number(props.procedureId))
const historyEntries = computed(() => {
  return procedureStore
    .getProcedureHistory(normalizedProcedureId.value)
    .filter((procedureEntry) => procedureEntry?.status === 'archived')
})

const formatVersion = (versionValue) => {
  if (versionValue === null || versionValue === undefined || versionValue === '') {
    return '1'
  }

  return String(versionValue)
}

const formatStatus = (statusValue) => {
  const statusString = String(statusValue ?? '').trim()

  if (statusString === '') {
    return 'Unknown status'
  }

  return statusString
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const loadHistory = async () => {
  if (!Number.isFinite(normalizedProcedureId.value) || normalizedProcedureId.value <= 0) {
    return
  }

  loadingHistory.value = true

  try {
    await procedureStore.fetchProcedureHistory(normalizedProcedureId.value)
  } finally {
    loadingHistory.value = false
  }
}

const toggleHistory = async () => {
  if (!showHistory.value) {
    await loadHistory()
  }

  showHistory.value = !showHistory.value
}

watch(normalizedProcedureId, () => {
  showHistory.value = false
  loadingHistory.value = false
})
</script>
